import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  serverTimestamp,
  Timestamp,
  collection,
  getDocs,
  query,
  where
} from 'firebase/firestore';
import { db } from '../../firebase';
import { UserProfile, UserPreferences, ChildProfile, ProfileType } from '../types';

export const userService = {
  // Crear documento de usuario en Firestore
  createUserProfile: async (uid: string, email: string, displayName?: string, profileType: ProfileType = 'tutor') => {
    console.log('[USER_SERVICE] Creando perfil de usuario, UID:', uid, 'Tipo:', profileType);
    try {
      const userRef = doc(db, 'users', uid);
      console.log('[USER_SERVICE] Referencia de documento creada');
      const userData: UserProfile = {
        uid,
        email,
        displayName: displayName || email.split('@')[0],
        createdAt: new Date(),
        updatedAt: new Date(),
        lastLoginAt: new Date(),
        preferences: {
          language: 'es',
          notifications: true,
          theme: 'auto',
          emailNotifications: true
        },
        onboardingCompleted: false,
        profileType,
        childrenIds: profileType === 'tutor' ? [] : undefined
      };
      
      console.log('[USER_SERVICE] Enviando datos a Firestore...');
      await setDoc(userRef, userData);
      console.log('[USER_SERVICE] Perfil de usuario creado exitosamente');
      return { success: true, data: userData };
    } catch (error: any) {
      console.error('[USER_SERVICE] Error al crear perfil:', error);
      console.error('[USER_SERVICE] Error code:', error.code);
      console.error('[USER_SERVICE] Error message:', error.message);
      return { success: false, error: error.message };
    }
  },

  // Obtener perfil de usuario
  getUserProfile: async (uid: string) => {
    console.log('[USER_SERVICE] Obteniendo perfil de usuario, UID:', uid);
    try {
      const userRef = doc(db, 'users', uid);
      console.log('[USER_SERVICE] Consultando documento en Firestore...');
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        console.log('[USER_SERVICE] Documento encontrado');
        const data = userDoc.data();
        // Convertir Timestamps a Dates
        const userData: UserProfile = {
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          lastLoginAt: data.lastLoginAt?.toDate() || new Date()
        } as UserProfile;
        
        console.log('[USER_SERVICE] Perfil obtenido exitosamente');
        return { success: true, data: userData };
      } else {
        console.log('[USER_SERVICE] Documento no encontrado');
        return { success: false, error: 'Usuario no encontrado' };
      }
    } catch (error: any) {
      console.error('[USER_SERVICE] Error al obtener perfil:', error);
      console.error('[USER_SERVICE] Error code:', error.code);
      console.error('[USER_SERVICE] Error message:', error.message);
      return { success: false, error: error.message };
    }
  },

  // Actualizar perfil de usuario
  updateUserProfile: async (uid: string, updates: Partial<UserProfile>) => {
    try {
      const userRef = doc(db, 'users', uid);
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(userRef, updateData);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },

  // Actualizar último login
  updateLastLogin: async (uid: string) => {
    console.log('[USER_SERVICE] Actualizando último login, UID:', uid);
    try {
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        lastLoginAt: serverTimestamp()
      });
      console.log('[USER_SERVICE] Último login actualizado exitosamente');
      return { success: true };
    } catch (error: any) {
      console.error('[USER_SERVICE] Error al actualizar último login:', error);
      console.error('[USER_SERVICE] Error code:', error.code);
      console.error('[USER_SERVICE] Error message:', error.message);
      return { success: false, error: error.message };
    }
  },

  // Actualizar preferencias
  updatePreferences: async (uid: string, preferences: Partial<UserPreferences>) => {
    try {
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        preferences,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },

  // Marcar onboarding como completado
  completeOnboarding: async (uid: string, emotion: string) => {
    console.log('[USER_SERVICE] Completando onboarding, UID:', uid, 'Emoción:', emotion);
    try {
      const userRef = doc(db, 'users', uid);
      await updateDoc(userRef, {
        onboardingCompleted: true,
        currentEmotion: emotion,
        updatedAt: serverTimestamp()
      });
      console.log('[USER_SERVICE] Onboarding completado exitosamente');
      return { success: true };
    } catch (error: any) {
      console.error('[USER_SERVICE] Error al completar onboarding:', error);
      console.error('[USER_SERVICE] Error code:', error.code);
      console.error('[USER_SERVICE] Error message:', error.message);
      return { success: false, error: error.message };
    }
  },

  // Crear perfil de menor (child profile)
  createChildProfile: async (tutorId: string, name: string, age: number) => {
    console.log('[USER_SERVICE] Creando perfil de menor, Tutor ID:', tutorId, 'Nombre:', name);
    try {
      const childId = `${tutorId}_${Date.now()}`;
      const childRef = doc(db, 'children', childId);
      
      const childData: ChildProfile = {
        uid: childId,
        name,
        age,
        createdAt: new Date(),
        tutorId,
        preferences: {
          language: 'es',
          theme: 'auto'
        },
        onboardingCompleted: false
      };
      
      await setDoc(childRef, childData);
      
      // Agregar ID del menor al perfil del tutor
      const tutorRef = doc(db, 'users', tutorId);
      const tutorDoc = await getDoc(tutorRef);
      
      if (tutorDoc.exists()) {
        const tutorData = tutorDoc.data();
        const childrenIds = tutorData.childrenIds || [];
        await updateDoc(tutorRef, {
          childrenIds: [...childrenIds, childId],
          updatedAt: serverTimestamp()
        });
      }
      
      console.log('[USER_SERVICE] Perfil de menor creado exitosamente');
      return { success: true, data: childData };
    } catch (error: any) {
      console.error('[USER_SERVICE] Error al crear perfil de menor:', error);
      return { success: false, error: error.message };
    }
  },

  // Obtener perfiles de menores de un tutor
  getChildrenProfiles: async (tutorId: string) => {
    console.log('[USER_SERVICE] Obteniendo perfiles de menores, Tutor ID:', tutorId);
    try {
      const childrenRef = collection(db, 'children');
      const q = query(childrenRef, where('tutorId', '==', tutorId));
      const querySnapshot = await getDocs(q);
      
      const children: ChildProfile[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        children.push({
          ...data,
          uid: doc.id,
          createdAt: data.createdAt?.toDate() || new Date()
        } as ChildProfile);
      });
      
      console.log('[USER_SERVICE] Perfiles de menores obtenidos:', children.length);
      return { success: true, data: children };
    } catch (error: any) {
      console.error('[USER_SERVICE] Error al obtener perfiles de menores:', error);
      return { success: false, error: error.message };
    }
  },

  // Obtener perfil de menor específico
  getChildProfile: async (childId: string) => {
    console.log('[USER_SERVICE] Obteniendo perfil de menor, ID:', childId);
    try {
      const childRef = doc(db, 'children', childId);
      const childDoc = await getDoc(childRef);
      
      if (childDoc.exists()) {
        const data = childDoc.data();
        const childData: ChildProfile = {
          ...data,
          uid: childDoc.id,
          createdAt: data.createdAt?.toDate() || new Date()
        } as ChildProfile;
        
        return { success: true, data: childData };
      } else {
        return { success: false, error: 'Perfil de menor no encontrado' };
      }
    } catch (error: any) {
      console.error('[USER_SERVICE] Error al obtener perfil de menor:', error);
      return { success: false, error: error.message };
    }
  },

  // Actualizar perfil de menor
  updateChildProfile: async (childId: string, updates: Partial<ChildProfile>) => {
    try {
      const childRef = doc(db, 'children', childId);
      await updateDoc(childRef, updates);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  },

  // Eliminar perfil de menor
  deleteChildProfile: async (tutorId: string, childId: string) => {
    console.log('[USER_SERVICE] Eliminando perfil de menor, ID:', childId);
    try {
      // Eliminar de la colección de children
      const childRef = doc(db, 'children', childId);
      // Nota: Firestore no tiene delete directo, se usa deleteDoc
      // Por ahora solo lo removemos de la lista del tutor
      
      // Remover ID del perfil del tutor
      const tutorRef = doc(db, 'users', tutorId);
      const tutorDoc = await getDoc(tutorRef);
      
      if (tutorDoc.exists()) {
        const tutorData = tutorDoc.data();
        const childrenIds = tutorData.childrenIds || [];
        await updateDoc(tutorRef, {
          childrenIds: childrenIds.filter((id: string) => id !== childId),
          updatedAt: serverTimestamp()
        });
      }
      
      console.log('[USER_SERVICE] Perfil de menor eliminado exitosamente');
      return { success: true };
    } catch (error: any) {
      console.error('[USER_SERVICE] Error al eliminar perfil de menor:', error);
      return { success: false, error: error.message };
    }
  }
};
